import React, { Component } from 'react';
import { translate } from 'react-i18next';
import auth from '@react-native-firebase/auth';
import {
  Field, reduxForm, SubmissionError, stopSubmit, InjectedFormProps,
} from 'redux-form';
import {
  Text, View, ScrollView, StyleSheet, Platform, TouchableOpacity, TextInput as RNTextInput,
} from 'react-native';
import { connect } from 'react-redux';
import { UserServiceClient } from 'fitbeat.api-clients';
import Accordion from 'react-native-collapsible/Accordion';
import {
  FoodPreference, BaseEquipment, EquipmentCombinations, EnabledFeatures, Equipment,
  MealPreferences,
} from 'fitbeat.models';
import { UserProfile, UserProfileService, NutritionService } from 'fitbeat.gym-core';
import { Dispatch } from 'redux';
import {
  Button,
  Heading,
  HeadingTypes,
} from '../components';
import Theme from '../theme';
import {
  FormError, FormKeyboardSpacer, TextInput, RadioButtonGroup,
} from '../ui/components';
import AccordingHeader from '../ui/components/AccordingHeader';
import { showError, showMessage } from '../ui/components/Toast';
import { UserDataConsumer } from '../utils';
import { GoogleAnalytics } from '../services/google-analytics';
import { ResponsiveLayout } from '../utils/ResponsiveLayout';
import { AllergenPreference, SettingsProfile } from './components';
import firebaseErrorHandler from '../utils/FirebaseErrors';
import {
  minLength6,
  isRequired,
  isPasswordSame,
} from '../utils/validator';
import { Permissions } from '../utils/Permissions';
import { getApiConfig } from '../config';
import { AuthTokenBuilder } from '../utils/authTokenBuilder';
import { PrivacySettings } from '../WorkoutPartners';
import { FoodGroupSelection, UnitMeasurementSystemSelection } from '../Nutrition/components';
import { setNutritionPreference, setUnitPreference } from '../Nutrition/actions';
import { defaultScale } from '../utils/scaling';
import { enabledFeaturesSelector } from '../auth/auth-selectors';
import { focusOnInput } from '../utils/FocusOnInput';
import { NavigatorService } from '../utils/NavigatorService';
import ResetAppState from '../utils/ResetAppStateHelper';
import { TranslateProp } from '../common-types';
import { EquipmentType, getEquipmentList } from '../utils/equipmentUtils';
import { IAppStore } from '../store/types';
import { Switch } from '../ui/components/Switch';

interface IFormSection {
  title: string;
  content: () => JSX.Element;
  isLast?: boolean;
}

interface IProps extends InjectedFormProps {
  t: TranslateProp;
  user: UserProfile;
  enabledFeatures: EnabledFeatures;
  userProfileService: UserProfileService;
  nutritionService: NutritionService;
  navigation: any;
  saveMealPreferences: (mealPreferences: MealPreferences) => void;
  onUnitPreferenceSet: () => void;
  initialize: (values: any) => void;
  dispatch: (action: any) => void;
  handleSubmit: (fn: (values: any) => void) => (event: any) => void;
  error?: string;
}

interface IState {
  forms: IFormSection[];
  error: string | null;
  privateProfile: boolean;
  isLoading: boolean;
  pushMessageSettings: {
    enabled: boolean;
  };
  emailNotificationSettings: {
    enableAnnoying: boolean;
    enableMarketing: boolean;
  };
  foodPreference: FoodPreference;
  useImperialUnit: boolean;
  initiallyActiveSection: number;
  selectedEquipment: EquipmentType;
  defaultEquipment: Equipment;
  allergens: string[];
  isHighlyActive: boolean;
  activeSection?: number;
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Theme.WHITE,
    padding: 15,
    ...Platform.select({
      web: {
        paddingHorizontal: 30,
        paddingVertical: 0,
      },
    }),
  },
  content: {
    paddingBottom: 15,
  },
  notificationSettingContainer: {
    paddingVertical: 4,
    ...Platform.select({
      web: {
        paddingVertical: 10,
        paddingRight: 10,
      },
    }),
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  notificationSettingText: {
    ...Theme.textStyle.bodyCopyReg,
    fontSize: defaultScale(14),
  },
  saveButtonContainer: {
    marginVertical: 15,
  },
  privacyOptionsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  privacySettingOption: {
    marginRight: 0,
  },
  privacyInfo: {
    fontSize: 14,
    paddingVertical: 5,
    color: Theme.GREY_MEDIUM,
  },
  radioOptionStyle: {
    ...Theme.textStyle.bodyCopyReg,
    paddingBottom: 0,
  },
  toggleContainer: {
    justifyContent: 'space-between',
    alignItems: 'center',
    flexDirection: 'row',
  },
  toggleExplanationText: {
    ...Theme.textStyle.bodyCopyReg,
    color: Theme.GREY_MEDIUM,
    maxWidth: defaultScale(270),
  },
});

class PreferencesTabComponent extends Component<IProps, IState> {
  public userServiceClient: UserServiceClient;

  public oldPassword: RNTextInput | null = null;

  public passwordInput: RNTextInput | null = null;

  public confirmPasswordInput: RNTextInput | null = null;

  constructor(props: IProps) {
    super(props);
    const { t, enabledFeatures } = props;
    const settings = props.user.userProfile;
    const preSelectedFoodPreference = (settings.mealPreferences && settings.mealPreferences.foodPreference)
      ? settings.mealPreferences.foodPreference
      : FoodPreference.AllFoodGroups;
    const preSelectedUnitSystemPreference = (settings.useImperialUnit);
    const equipment = EnabledFeatures.calculateEquipmentSettings(enabledFeatures, settings.defaultEquipment);
    const equipmentCombo = BaseEquipment.getEquipmentCombination(equipment);
    const defaultEquipment = EnabledFeatures.getEquipmentListFromEquipmentCombination(enabledFeatures);

    let initialEquipmentType: EquipmentType = 'bodyweight';
    if (equipmentCombo === EquipmentCombinations.BENCH_HUB_DUMBBELLS) {
      initialEquipmentType = 'benchAndDumbbells';
    } else if (equipmentCombo === EquipmentCombinations.DUMBBELLS) {
      initialEquipmentType = 'dumbbells';
    } else if (equipmentCombo === EquipmentCombinations.BENCH_BANDS) {
      initialEquipmentType = 'smartHomeGym';
    }

    const {
      pushMessageSettings = {
        enabled: true,
      },
      emailNotificationSettings = {
        enableAnnoying: true,
        enableMarketing: true,
      },
      privateProfile = false,
    } = settings;

    const firebaseProviderId = this.getFirebaseProviderId();
    const forms: IFormSection[] = [];
    let changePasswordForm: IFormSection | null = null;
    if (firebaseProviderId === 'password') {
      changePasswordForm = {
        title: t('resetPassword'),
        content: () => this.changePasswordFields(),
      };
      forms.push(changePasswordForm);
    }

    const notificationForm = {
      title: t('notificationSettings'),
      content: () => this.notificationSettingFields(),
    };
    const privacyForm = {
      title: t('privacySettings'),
      content: () => this.privacySettingFields(),
    };
    const equipmentForm = {
      title: t('defaultWorkoutEquipment'),
      content: () => this.equipmentUpdate(),
      isLast: true,
    };
    const foodGroupForm = {
      title: t('dietaryPreference'),
      content: () => this.foodGroupUpdate(),
    };

    const allergenForm = {
      title: t('allergenSection'),
      content: () => this.allergenUpdate(),
    };

    const unitSystemForm = {
      title: t('unitMeasurementSystemPreference'),
      content: () => this.unitSystemUpdate(),
    };

    forms.push(
      notificationForm,
      privacyForm,
      foodGroupForm,
      allergenForm,
      unitSystemForm,
    );

    if (EnabledFeatures.canSelectEquipment(enabledFeatures)) {
      forms.push(
        equipmentForm,
      );
    }
    this.state = {
      forms,
      privateProfile,
      isLoading: false,
      pushMessageSettings,
      emailNotificationSettings,
      foodPreference: preSelectedFoodPreference,
      useImperialUnit: preSelectedUnitSystemPreference,
      initiallyActiveSection: Platform.OS === 'web' ? 0 : -1,
      selectedEquipment: initialEquipmentType,
      allergens: settings.allergens || [],
      isHighlyActive: !!settings.isHighlyActive,
    };
    this.userServiceClient = new UserServiceClient(
      getApiConfig(),
      fetch,
      console.log,
      new AuthTokenBuilder(),
      new GoogleAnalytics(),
    );
  }

  public async componentDidMount() {
    this.setError(null);
    if (Platform.OS !== 'web') {
      try {
        const permissionGranted = await Permissions.checkNotificationPermission();
        const toggleValue = permissionGranted && this.state.pushMessageSettings.enabled;
        await this.setNotification(toggleValue, false /* showMessage */);
      } catch (error) {
        this.setError(error instanceof Error ? error.message : String(error));
      }
    }
  }

  public setNotification = async (enabled: boolean, shouldShowMessage: boolean = true) => {
    this.setError(null);

    if (enabled) {
      const permissionGranted = await Permissions.checkNotificationPermission();
      if (permissionGranted) {
        return this.setNotificationStateAndSave(true, shouldShowMessage);
      }

      const allowNotifications = await Permissions.requestNotificationPermission();
      if (allowNotifications) {
        return this.setNotificationStateAndSave(true, shouldShowMessage);
      }
    } else {
      await this.setNotificationStateAndSave(false, shouldShowMessage);
    }
  };

  public setAnnoyingEmail = async (enableAnnoying: boolean) => {
    this.setError(null);
    await this.setState((prevState) => ({
      emailNotificationSettings: {
        ...prevState.emailNotificationSettings,
        enableAnnoying,
      },
    }));
    this.saveNotificationSettings().catch((error) => {
      this.setError(error);
    });
  };

  public setIsLoading = (value: boolean) => {
    this.setState({ isLoading: value });
  };

  public getFirebaseProviderId = () => auth().currentUser.providerData[0].providerId;

  public clearForm = () => {
    const { initialize } = this.props;
    initialize({
      oldPassword: '',
      password: '',
      confirmPassword: '',
    });
  };

  public changePassword = async (values: { password: string; oldPassword: string; }) => {
    const { t } = this.props;
    const { password, oldPassword } = values;
    const user = await auth().currentUser;

    try {
      this.setIsLoading(true);
      const credential = auth.EmailAuthProvider.credential(user.email, oldPassword);
      await user.reauthenticateWithCredential(credential);
      await user.updatePassword(password);
      await this.userServiceClient.changedPassword();
      showMessage(t('passwordChangedSuccess'));
      this.setActiveIndex(-1);
    } catch (error) {
      throw new SubmissionError({ _error: firebaseErrorHandler(error) });
    } finally {
      this.clearForm();
      this.setIsLoading(false);
    }
  };

  public saveNotificationSettings = async (shouldShowMessage: boolean = true) => {
    const { pushMessageSettings, emailNotificationSettings } = this.state;
    const { t, user: { userProfile, userProfileService } } = this.props;

    try {
      await userProfileService.updateUserProfile({
        ...userProfile,
        pushMessageSettings,
        emailNotificationSettings,
      });
      if (shouldShowMessage) {
        showMessage(t('notificationsUpdatedSuccess'));
      }
    } catch (error) {
      this.setError(error);
    }
  };

  public setError = (error: string | null) => {
    this.setState({ error });
  };

  public changePasswordFields = () => {
    const {
      t,
      error,
      handleSubmit,
    } = this.props;
    const { isLoading } = this.state;

    return (
      <View>
        <ResponsiveLayout
          col={2}
          thresholdSize='medium'
        >
          <Field
            forwardRef
            isSecure
            name='oldPassword'
            component={TextInput}
            autoCapitalize='none'
            placeholder={t('oldPassword')}
            keyboardType='default'
            returnKeyType='next'
            validate={isRequired}
            ref={(ref) => { this.oldPassword = ref; }}
            onSubmitEditing={() => focusOnInput(this.passwordInput)}
          />
          <Field
            forwardRef
            isSecure
            name='password'
            component={TextInput}
            autoCapitalize='none'
            placeholder={t('newPassword')}
            keyboardType='default'
            returnKeyType='next'
            validate={[isRequired, minLength6]}
            ref={(ref) => { this.passwordInput = ref; }}
            onSubmitEditing={() => focusOnInput(this.confirmPasswordInput)}
          />
          <Field
            forwardRef
            isSecure
            name='confirmPassword'
            component={TextInput}
            autoCapitalize='none'
            placeholder={t('confirmNewPassword')}
            keyboardType='default'
            returnKeyType='done'
            validate={[isRequired, isPasswordSame]}
            onSubmitEditing={handleSubmit(this.changePassword)}
            ref={(ref) => { this.confirmPasswordInput = ref; }}
          />
        </ResponsiveLayout>
        <FormError error={error} />
        <Button
          isLoading={isLoading}
          title={t('resetPassword')}
          fontColor={Theme.WHITE}
          onPress={handleSubmit(this.changePassword)}
          containerStyle={styles.saveButtonContainer}
        />
      </View>
    );
  };

  public notificationSettingFields = () => {
    const { t } = this.props;
    const { pushMessageSettings, emailNotificationSettings } = this.state;

    return (
      <View>
        <View style={styles.notificationSettingContainer}>
          <Heading
            title={t('notificationsMobile')}
            headingType={HeadingTypes.HEADING_THREE}
            style={styles.notificationSettingText}
          />
          <Switch
            value={this.state.pushMessageSettings.enabled}
            onValueChange={(value) => this.setNotification(value, true)}
          />
        </View>
        <View style={styles.notificationSettingContainer}>
          <Heading
            title={t('notificationsEmail')}
            headingType={HeadingTypes.HEADING_THREE}
            style={styles.notificationSettingText}
          />
          <Switch
            value={emailNotificationSettings.enableAnnoying}
            onValueChange={this.setAnnoyingEmail}
          />
        </View>
      </View>
    );
  };

  public setProfileToPrivate = async () => {
    await this.setState({ privateProfile: true }, this.updateProfilePrivacy);
  };

  public setProfileToPublic = async () => {
    await this.setState({ privateProfile: false }, this.updateProfilePrivacy);
  };

  public updateProfilePrivacy = async () => {
    const { t, user: { userProfileService } } = this.props;
    const { privateProfile } = this.state;

    try {
      await userProfileService.setUserProfilePrivacySetting(privateProfile);
      showMessage(t('privacyUpdatedSuccess'));
    } catch (error) {
      this.setError(error);
    }
  };

  public privacySettingFields = () => {
    const { privateProfile } = this.state;

    return (
      <View>
        <PrivacySettings
          privateProfile={privateProfile}
          setProfileToPrivate={this.setProfileToPrivate}
          setProfileToPublic={this.setProfileToPublic}
        />
      </View>
    );
  };

  public onAllergenSelected = (allergen: string) => {
    const newAllergenSelection = [...this.state.allergens, allergen];
    this.updateAllergensOnUserprofile(newAllergenSelection).catch((error) => {
      this.setError(error);
    });
  };

  public onAllergenUnSelected = (allergen: string) => {
    const newAllergenSelection = this.state.allergens.filter((item) => item !== allergen);
    this.updateAllergensOnUserprofile(newAllergenSelection).catch((error) => {
      this.setError(error);
    });
  };

  public updateAllergensOnUserprofile = async (newAllergenSelection: string[]) => {
    const { t, user: { userProfile, userProfileService } } = this.props;
    const allergensBeforeUpdate = this.state.allergens;
    this.setState({
      allergens: newAllergenSelection,
    });
    try {
      const newUserProfile = { ...userProfile, allergens: newAllergenSelection };
      await userProfileService.updateUserProfile(newUserProfile);
      showMessage(t('allergenUpdateSuccess'));
    } catch (error) {
      this.setState({
        allergens: allergensBeforeUpdate,
      });
      showError(t('allergenUpdateFailure'));
    }
  };

  public equipmentUpdate = () => {
    const { t } = this.props;
    return (
      <View style={{ paddingLeft: defaultScale(4) }}>
        <View style={{ marginBottom: defaultScale(35) }}>
          <RadioButtonGroup
            options={[
              {
                name: t('bodyweightNoEquipment'),
                isSelected: this.state.selectedEquipment === 'bodyweight',
                setOption: () => this.setEquipmentAndSave('bodyweight'),
                style: styles.radioOptionStyle,
              },
              {
                name: t('dumbbellsOwnSet'),
                isSelected: this.state.selectedEquipment === 'dumbbells',
                setOption: () => this.setEquipmentAndSave('dumbbells'),
                style: styles.radioOptionStyle,
              },
              {
                name: t('benchAndDumbbells'),
                isSelected: this.state.selectedEquipment === 'benchAndDumbbells',
                setOption: () => this.setEquipmentAndSave('benchAndDumbbells'),
                style: styles.radioOptionStyle,
              },
              {
                name: t('fitillionBenchAndBands'),
                isSelected: this.state.selectedEquipment === 'smartHomeGym',
                setOption: () => this.setEquipmentAndSave('smartHomeGym'),
                style: styles.radioOptionStyle,
              },
            ]}
            radioOptionContainer={{ alignItems: 'center' }}
          />
        </View>
      </View>
    );
  };

  public setEquipmentAndSave = async (equipmentType: EquipmentType) => {
    const { t, user: { userProfile, userProfileService } } = this.props;
    const equipment = getEquipmentList(equipmentType);

    try {
      this.setState({ selectedEquipment: equipmentType });
      userProfile.defaultEquipment = equipment;
      await userProfileService.updateUserProfile(userProfile);
      showMessage(t('equipmentUpdateSuccess'));
    } catch (error) {
      showError(t('equipmentUpdateFailure'));
    }
  };

  public allergenUpdate = () => {
    const { allergens } = this.state;
    return (
      <AllergenPreference
        selectedAllergens={allergens}
        onSelected={this.onAllergenSelected}
        onUnselected={this.onAllergenUnSelected}
      />
    );
  };

  public setIsHighlyActive = async (isHighlyActive: boolean) => {
    const { t, user: { userProfile, userProfileService } } = this.props;
    const { foodPreference } = this.state;
    const settingBeforeUpdate = this.state.isHighlyActive;
    this.setState({ isHighlyActive });
    try {
      const newUserProfile = {
        ...userProfile,
        isHighlyActive,
        mealPreferences: { ...userProfile.mealPreferences, foodPreference },
      };
      await userProfileService.updateUserProfile(newUserProfile);
      showMessage(t('portionSizeUpdateSuccess'));
    } catch (error) {
      this.setState({
        isHighlyActive: settingBeforeUpdate,
      });
      showError(t('portionSizeUpdateFailure'));
    }
  };

  public foodGroupUpdate = () => {
    const { t } = this.props;
    return (
      <View>
        <FoodGroupSelection
          selectedFoodPreference={this.state.foodPreference}
          setFoodPreferenceToAllFoodGroups={() => this.updateFoodPreference(FoodPreference.AllFoodGroups)}
          setFoodPreferenceToVegetarian={() => this.updateFoodPreference(FoodPreference.Vegetarian)}
          setFoodPreferenceToVegan={() => this.updateFoodPreference(FoodPreference.Vegan)}
        />
        <View style={styles.toggleContainer}>
          <Text style={{ ...Theme.textStyle.bodyCopyBold }}>
            {t('auth:hasActiveLifestyle')}
          </Text>
          <Switch
            value={this.state.isHighlyActive}
            onValueChange={this.setIsHighlyActive}
          />
        </View>
        <Text style={styles.toggleExplanationText}>
          {t('auth:portionSizeSetting')}
        </Text>
      </View>
    );
  };

  public unitSystemUpdate = () => (
    <View>
      <UnitMeasurementSystemSelection
        selectedUnitSystemImperial={this.state.useImperialUnit}
        setUnitSystemPreferenceToImperial={() => {
          this.setUnitSystemPreference(true).catch((error) => {
            this.setError(error);
          });
        }}
        setUnitSystemPreferenceToMetric={() => {
          this.setUnitSystemPreference(false).catch((error) => {
            this.setError(error);
          });
        }}

      />
    </View>
  );

  public updateFoodPreference = async (foodPreference: FoodPreference) => {
    const { t, user: { userProfile, nutritionService }, saveMealPreferences } = this.props;
    try {
      this.setState({ foodPreference });
      await nutritionService.saveFoodPreferences(
        foodPreference,
        this.state.useImperialUnit,
        userProfile.longTermGoal,
        userProfile.gender,
      );
      saveMealPreferences({ foodPreference, dietGoal: userProfile.longTermGoal });
      showMessage(t('foodGroupUpdateSuccess'));
    } catch (error) {
      this.setError(error);
    }
  };

  public setUnitSystemPreference = async (useImperialUnit: boolean) => {
    const { t, user: { nutritionService }, onUnitPreferenceSet } = this.props;
    try {
      this.setState({ useImperialUnit });
      await nutritionService.saveUnitPreference(useImperialUnit);
      onUnitPreferenceSet();
      ResetAppState().catch((error) => {
        this.setError(error);
      });
      NavigatorService.navigateContainerOne('AuthLoading');
    } catch (error) {
      this.setError(error);
    }
  };

  public setActiveIndex = (value: number) => {
    this.setState({ activeSection: value });
  };

  public clearSubmitForm = () => {
    this.props.dispatch(stopSubmit('PreferencesTabForm', {}));
  };

  public renderHeader = (section: any, index: number, isActive: boolean) => (
    <AccordingHeader
      index={index}
      section={section}
      isActive={isActive}
      setActiveIndex={this.setActiveIndex}
      onPress={this.clearSubmitForm}
    />
  );

  public renderContent = (section: any) => (
    <View style={styles.content}>
      {section.content()}
    </View>
  );

  public render() {
    const { user } = this.props;
    const { initiallyActiveSection } = this.state;

    return (
      <View style={[styles.container, { height: '100%' }]}>
        <FormKeyboardSpacer>
          <ScrollView keyboardShouldPersistTaps='handled'>
            <SettingsProfile userProfile={user.userProfile} navigation={this.props.navigation} />
            <View>
              <Accordion
                sections={this.state.forms}
                renderHeader={this.renderHeader}
                renderContent={this.renderContent}
                touchableComponent={TouchableOpacity}
                activeSection={this.state.activeSection}
                initiallyActiveSection={initiallyActiveSection}
              />
            </View>
          </ScrollView>
        </FormKeyboardSpacer>
      </View>
    );
  };

  private setNotificationStateAndSave = async (state: boolean, shouldShowMessage: boolean = true) => {
    this.setState((prevState) => ({
      pushMessageSettings: {
        ...prevState.pushMessageSettings,
        enabled: state,
      },
    }), () => {
      this.saveNotificationSettings(shouldShowMessage).catch((error) => {
        this.setError(error);
      });
    });
  };
}

const mapStateToProps = (state: IAppStore) => ({
  enabledFeatures: enabledFeaturesSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  saveMealPreferences: (mealPreferences: MealPreferences) => {
    dispatch(setNutritionPreference(mealPreferences));
  },
  onUnitPreferenceSet: () => dispatch(setUnitPreference()),
});

const WithRedux = connect(mapStateToProps, mapDispatchToProps)(PreferencesTabComponent);
const WithTranslation = translate(['settings', 'formFields', 'nutrition', 'auth'], { wait: true })(WithRedux);
const WithUserData = UserDataConsumer(WithTranslation);
const PreferencesTab = reduxForm({ form: 'PreferencesTabForm' })(WithUserData);

export default PreferencesTab;
